import { ActionType } from '../../../core/redux/actionType';
import { PreviousRater } from './models';
import { Endpoints } from '../../../core/api/endpoints';
import { makeRsaaTypes, makeApiAction, ApiReducer, makeApiReducer } from '../../../core/api/rsaa';
import { AppState } from '../../../core/redux/appState';
import { getLoading, addLoading, addError, noLoadingNoError } from '../../../core/api/module';
import * as R from 'ramda';
import { toMap } from '../../../language/arrays';


const KEY_ACTION = ActionType.PreviousRatersRequest;
const ACTIONS = [
  ActionType.PreviousRatersRequest,
  ActionType.PreviousRatersResponse,
  ActionType.PreviousRatersError,
];


type Payload = PreviousRater[];


type Meta = {
  projectId: number;
}


/**
 * Dispatch this action to retrieve all raters previously associated with the provided project (and previously assigned
 * to rate the provided participant).
 *
 * @param projectId - the database Id for the project for which we're retrieving raters
 * @param participantId - the database Id for the specific project participant whose raters we're retrieving
 */
export const previousRatersAction = (projectId: number, participantId: number) => makeApiAction<Payload, Meta>(
  {
    method: 'POST',
    endpoint: Endpoints.PreviousRaters,
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      projectId: projectId,
      participantId: participantId,
    }),
    types: makeRsaaTypes<Payload, Meta>(
      ACTIONS,
      {
        projectId: projectId,
      },
    ),
    bailout:  (state: AppState) => !!getLoading(state, KEY_ACTION, `${projectId}`),
  },
);

const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const metaTag = `${action.meta.projectId}`;

  if (isLoading) {
    return addLoading(state, KEY_ACTION, metaTag);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, metaTag);
  }

  const successState = noLoadingNoError(state, KEY_ACTION, metaTag);

  const mappedRaters = !!payload ? toMap<PreviousRater>(payload, rater => `${rater.raterId}`) : undefined;

  return R.mergeDeepRight(
    successState,
    {
      previousRaters: { [`${action.meta.projectId}`]: mappedRaters },
    },
  );
};

export const previousRatersReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
